/**
 * Shared aws-cn credential provider for the Event Hub module.
 *
 * Both the AWSECEventHub client (EventHubClient) and the Octopus Flow
 * Management client (FlowManagementClient) authenticate the same way:
 *
 *   Federate id_token → EventHub Cognito Identity Pool (aws-cn / BJS)
 *   → temp AWS creds → SigV4 (execute-api) → target API.
 *
 * The credential provider is memoized once (the SDK provider caches temp creds
 * and only re-exchanges when they expire); the id_token is resolved lazily per
 * refresh so the memoized provider stays valid across the session.
 */

import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import type { AwsCredentialIdentityProvider } from '@smithy/types';
import { appConfig } from '../../config';
import { getIdToken } from '../../context/AuthContext';

// Fixed aws-cn platform regions (not per-stage config): Cognito Identity is only
// available in BJS (cn-north-1), while the Event Hub / Octopus APIs live in ZHY
// (cn-northwest-1). The cred exchange uses the pool's region; SigV4 MUST sign
// for the API's region or the request 403s on credential scope.
export const COGNITO_REGION = 'cn-north-1'; // BJS — pool
export const API_REGION = 'cn-northwest-1'; // ZHY — API Gateway (execute-api signing)

let _credentials: AwsCredentialIdentityProvider | undefined;

/**
 * Build (once) the credential provider that exchanges the Federate id_token for
 * temp AWS creds via the EventHub Cognito Identity Pool (aws-cn). A
 * NotAuthorizedException / "Invalid login token" from the provider means the
 * OIDC provider or aud doesn't match what the pool trusts.
 */
export function getEventHubCredentials(): AwsCredentialIdentityProvider {
  if (_credentials) return _credentials;

  const { identityPoolId, identityProvider } = appConfig.eventHub;
  if (!identityPoolId || identityPoolId === 'TBD') {
    throw new Error(`[EventHub] No identity pool configured for stage "${appConfig.stage}"`);
  }

  _credentials = fromCognitoIdentityPool({
    identityPoolId,
    logins: {
      [identityProvider]: async () => {
        const idToken = getIdToken();
        if (!idToken) {
          throw new Error('[EventHub] Not authenticated — no id_token available');
        }
        return idToken;
      },
    },
    clientConfig: { region: COGNITO_REGION },
  });
  return _credentials;
}
